/**
 * CalendarPage
 * Main calendar view with unified Events + Tasks
 * ⭐ Consumes /api/calendar with granular backend filters
 */

import React, { useState, useEffect } from 'react'
import { AlertTriangle, ChevronLeft, ChevronRight, Plus, Filter, Flag, ListChecks, Calendar as CalendarIcon } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/utils/cn'
import { CalendarGrid } from '../components/calendar/CalendarGrid'
import { CalendarWeekView } from '../components/calendar/CalendarWeekView'
import { CalendarDayView } from '../components/calendar/CalendarDayView'
import { CalendarSidebar } from '../components/calendar/CalendarSidebar'
import { CreateEventModal } from '../components/calendar/CreateEventModal'
import { CalendarItemDetailsModal } from '../components/calendar/CalendarItemDetailsModal'
import { calendarService } from '../services/calendar-service'
import type {
  CalendarData,
  CalendarFilters,
  CalendarItem,
  EventType,
  TaskCategory,
  TaskStatus
} from '../types/calendar'
import { toast } from 'sonner'

type ViewMode = 'month' | 'week' | 'day'

const EVENT_TYPE_OPTIONS: [EventType, string][] = [
  ['AUDIENCIA', 'Audiência'],
  ['REUNIAO', 'Reunião'],
  ['COMPROMISSO', 'Compromisso'],
  ['OUTRO', 'Outro']
]

const TASK_CATEGORY_OPTIONS: [TaskCategory, string][] = [
  ['PRAZO_PROCESSUAL', 'Prazo Processual'],
  ['ACAO_INTERNA', 'Ação Interna'],
  ['FOLLOW_UP', 'Follow-up'],
  ['DOCUMENTO', 'Documento']
]

const TASK_STATUS_OPTIONS: [TaskStatus, string][] = [
  ['PENDING', 'Pendente'],
  ['IN_PROGRESS', 'Em Andamento'],
  ['FOR_REVIEW', 'Em Revisão'],
  ['COMPLETED', 'Concluída']
]

/**
 * CalendarPage Component
 */
export const CalendarPage: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [viewMode, setViewMode] = useState<ViewMode>('month')
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null)
  const [loading, setLoading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedItem, setSelectedItem] = useState<CalendarItem | null>(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)

  // ⭐ Granular filters state
  const [filters, setFilters] = useState<CalendarFilters>({
    startDate: undefined,
    endDate: undefined,
    itemTypes: undefined,
    eventTypes: undefined,
    taskCategories: undefined,
    taskStatuses: undefined,
    priorities: undefined,
    upcomingOnly: false,
    overdueOnly: false
  })

  /**
   * Load calendar data
   */
  const loadCalendarData = async () => {
    setLoading(true)
    try {
      // Calculate start and end dates based on view mode
      let startDate: string
      let endDate: string

      if (viewMode === 'month') {
        // Month view: first and last day of month
        const year = currentDate.getFullYear()
        const month = currentDate.getMonth()
        startDate = new Date(year, month, 1).toISOString().split('T')[0]
        endDate = new Date(year, month + 1, 0).toISOString().split('T')[0]
      } else if (viewMode === 'week') {
        // Week view: start of week (Sunday) to end of week (Saturday)
        const weekStart = new Date(currentDate)
        weekStart.setDate(currentDate.getDate() - currentDate.getDay())
        weekStart.setHours(0, 0, 0, 0)

        const weekEnd = new Date(weekStart)
        weekEnd.setDate(weekStart.getDate() + 6)

        startDate = weekStart.toISOString().split('T')[0]
        endDate = weekEnd.toISOString().split('T')[0]
      } else {
        // Day view: single day
        startDate = currentDate.toISOString().split('T')[0]
        endDate = startDate
      }

      // ⭐ Determine itemTypes based on active filters
      let itemTypes: ('event' | 'task')[] | undefined = undefined

      // If only event filters are active, show only events
      if (filters.eventTypes && filters.eventTypes.length > 0 && !filters.taskCategories && !filters.taskStatuses) {
        itemTypes = ['event']
      }
      // If only task filters are active, show only tasks
      else if ((filters.taskCategories && filters.taskCategories.length > 0) || (filters.taskStatuses && filters.taskStatuses.length > 0)) {
        if (!filters.eventTypes || filters.eventTypes.length === 0) {
          itemTypes = ['task']
        }
      }

      // Merge with user filters ⭐
      const finalFilters: CalendarFilters = {
        ...filters,
        startDate,
        endDate,
        itemTypes, // ⭐ Add itemTypes to control what to fetch
        limit: 200 // Max limit allowed by backend
      }

      console.log('Loading calendar with filters:', finalFilters)

      const data = await calendarService.getCalendarData(finalFilters)
      setCalendarData(data)
    } catch (error: unknown) {
      console.error('Error loading calendar:', error)
      toast.error('Erro ao carregar calendário')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCalendarData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, viewMode, JSON.stringify(filters)])

  /**
   * Navigation
   */
  const goToPrevious = () => {
    if (viewMode === 'month') {
      setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
    } else if (viewMode === 'week') {
      setCurrentDate(prev => {
        const newDate = new Date(prev)
        newDate.setDate(prev.getDate() - 7)
        return newDate
      })
    } else {
      setCurrentDate(prev => {
        const newDate = new Date(prev)
        newDate.setDate(prev.getDate() - 1)
        return newDate
      })
    }
  }

  const goToNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
    } else if (viewMode === 'week') {
      setCurrentDate(prev => {
        const newDate = new Date(prev)
        newDate.setDate(prev.getDate() + 7)
        return newDate
      })
    } else {
      setCurrentDate(prev => {
        const newDate = new Date(prev)
        newDate.setDate(prev.getDate() + 1)
        return newDate
      })
    }
  }

  const goToToday = () => {
    setCurrentDate(new Date())
  }

  /**
   * Handle item click - show details modal
   */
  const handleItemClick = (item: CalendarItem) => {
    setSelectedItem(item)
    setShowDetailsModal(true)
  }

  /**
   * Handle modal close
   */
  const handleDetailsModalClose = () => {
    setShowDetailsModal(false)
    setSelectedItem(null)
  }

  /**
   * Handle item update - reload calendar
   */
  const handleItemUpdate = () => {
    loadCalendarData()
  }

  /**
   * Filter handlers
   */
  const toggleEventType = (type: EventType) => {
    setFilters(prev => {
      const current = prev.eventTypes || []
      const hasType = current.includes(type)

      const newEventTypes = hasType
        ? current.filter(t => t !== type)
        : [...current, type]

      console.log('Toggle Event Type:', { type, hasType, current, newEventTypes })

      return {
        ...prev,
        eventTypes: newEventTypes.length > 0 ? newEventTypes : undefined
      }
    })
  }

  const toggleTaskCategory = (category: TaskCategory) => {
    setFilters(prev => {
      const current = prev.taskCategories || []
      const hasCategory = current.includes(category)

      return {
        ...prev,
        taskCategories: hasCategory
          ? current.filter(c => c !== category)
          : [...current, category]
      }
    })
  }

  const toggleTaskStatus = (status: TaskStatus) => {
    setFilters(prev => {
      const current = prev.taskStatuses || []
      const hasStatus = current.includes(status)

      return {
        ...prev,
        taskStatuses: hasStatus
          ? current.filter(s => s !== status)
          : [...current, status]
      }
    })
  }

  const clearFilters = () => {
    setFilters({
      startDate: undefined,
      endDate: undefined,
      itemTypes: undefined,
      eventTypes: undefined,
      taskCategories: undefined,
      taskStatuses: undefined,
      priorities: undefined,
      upcomingOnly: false,
      overdueOnly: false
    })
  }

  // Format date display based on view mode
  const getDateDisplay = (): string => {
    if (viewMode === 'month') {
      return currentDate.toLocaleDateString('pt-BR', {
        month: 'long',
        year: 'numeric'
      })
    } else if (viewMode === 'week') {
      const weekStart = new Date(currentDate)
      weekStart.setDate(currentDate.getDate() - currentDate.getDay())

      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekStart.getDate() + 6)

      return `${weekStart.getDate()} - ${weekEnd.getDate()} de ${weekStart.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`
    } else {
      return currentDate.toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    }
  }

  // Helper function to get week start date
  const getWeekStart = (): Date => {
    const weekStart = new Date(currentDate)
    weekStart.setDate(currentDate.getDate() - currentDate.getDay())
    weekStart.setHours(0, 0, 0, 0)
    return weekStart
  }

  const activeFilterCount =
    (filters.eventTypes?.length ?? 0) + (filters.taskCategories?.length ?? 0) + (filters.taskStatuses?.length ?? 0)

  const stats = calendarData?.stats
    ? [
        { label: 'Eventos', value: calendarData.stats.totalEvents, icon: CalendarIcon },
        { label: 'Tarefas', value: calendarData.stats.totalTasks, icon: ListChecks },
        { label: 'Atrasadas', value: calendarData.stats.overdueTasks, icon: AlertTriangle, alert: calendarData.stats.overdueTasks > 0 },
        { label: 'Alta Prioridade', value: calendarData.stats.highPriorityCount, icon: Flag }
      ]
    : []

  const filterGroups: { label: string; options: { value: string; label: string; active: boolean; toggle: () => void }[] }[] = [
    {
      label: 'Tipos de Evento',
      options: EVENT_TYPE_OPTIONS.map(([value, label]) => ({
        value, label, active: !!filters.eventTypes?.includes(value), toggle: () => toggleEventType(value)
      }))
    },
    {
      label: 'Categorias de Tarefa',
      options: TASK_CATEGORY_OPTIONS.map(([value, label]) => ({
        value, label, active: !!filters.taskCategories?.includes(value), toggle: () => toggleTaskCategory(value)
      }))
    },
    {
      label: 'Status de Tarefa',
      options: TASK_STATUS_OPTIONS.map(([value, label]) => ({
        value, label, active: !!filters.taskStatuses?.includes(value), toggle: () => toggleTaskStatus(value)
      }))
    }
  ]

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <CalendarIcon className="size-6 shrink-0 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Calendário</h1>
          </div>
          <p className="text-sm text-muted-foreground">Eventos e Tarefas Unificados</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="size-4" /> Novo Evento
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPrevious} aria-label="Período anterior">
            <ChevronLeft className="size-4" />
          </Button>
          <h2 className="min-w-0 flex-1 text-center text-base font-semibold capitalize sm:min-w-[250px] sm:text-lg">
            {getDateDisplay()}
          </h2>
          <Button variant="outline" size="icon" onClick={goToNext} aria-label="Próximo período">
            <ChevronRight className="size-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday}>Hoje</Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={viewMode} onValueChange={(next) => setViewMode(next as ViewMode)}>
            <TabsList>
              <TabsTrigger value="month">Mês</TabsTrigger>
              <TabsTrigger value="week">Semana</TabsTrigger>
              <TabsTrigger value="day">Dia</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} aria-expanded={showFilters}>
            <Filter className="size-4" />
            Filtros
            {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
        <div className="min-w-0 lg:shrink-0">
          <CalendarSidebar
            currentDate={currentDate}
            onDateSelect={(date) => {
              setCurrentDate(date)
              setViewMode('day')
            }}
            upcomingItems={calendarData?.items.filter(item => {
              const itemDate = new Date(item.date)
              const today = new Date()
              return itemDate >= today
            }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())}
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {showFilters && (
            <Card size="sm">
              <CardContent className="flex flex-col gap-4">
                {filterGroups.map((group) => (
                  <div key={group.label} className="flex flex-col gap-2">
                    <span className="text-sm font-medium">{group.label}</span>
                    <div className="flex flex-wrap gap-2">
                      {group.options.map((option) => (
                        <Button
                          key={option.value}
                          type="button"
                          size="sm"
                          variant={option.active ? 'default' : 'outline'}
                          aria-pressed={option.active}
                          onClick={option.toggle}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="flex justify-end">
                  <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
                    Limpar Filtros
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {stats.length > 0 && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {stats.map(({ label, value, icon: Icon, alert }) => (
                <Card key={label} size="sm">
                  <CardContent className="flex items-center gap-3">
                    <Icon className={cn('size-5 shrink-0', alert ? 'text-destructive' : 'text-muted-foreground')} aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="truncate text-xs text-muted-foreground">{label}</p>
                      <p className={cn('text-lg font-semibold tabular-nums', alert && 'text-destructive')}>{value}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex flex-col gap-2" aria-busy="true">
              <span className="sr-only">Carregando calendário...</span>
              <Skeleton className="h-96" />
            </div>
          ) : calendarData ? (
            <>
              {viewMode === 'month' && (
                <CalendarGrid
                  items={calendarData.items}
                  currentDate={currentDate}
                  onDateClick={(date) => {
                    setCurrentDate(date)
                    setViewMode('day')
                  }}
                  onItemClick={handleItemClick}
                />
              )}

              {viewMode === 'week' && (
                <CalendarWeekView
                  items={calendarData.items}
                  weekStart={getWeekStart()}
                  onDateClick={(date) => {
                    setCurrentDate(date)
                    setViewMode('day')
                  }}
                  onItemClick={handleItemClick}
                />
              )}

              {viewMode === 'day' && (
                <CalendarDayView
                  items={calendarData.items}
                  date={currentDate}
                  onItemClick={handleItemClick}
                />
              )}
            </>
          ) : (
            <div className="flex h-96 items-center justify-center rounded-lg border bg-muted/40">
              <p className="text-sm text-muted-foreground">Nenhum dado disponível</p>
            </div>
          )}
        </div>
      </div>

      <CreateEventModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={loadCalendarData}
      />

      <CalendarItemDetailsModal
        item={selectedItem}
        isOpen={showDetailsModal}
        onClose={handleDetailsModalClose}
        onUpdate={handleItemUpdate}
      />
    </div>
  )
}
